import { array } from 'fp-ts';
import { Application, NewRelicEventType } from '@mews/enums';
import { FeatureFlag } from '@mews-commander/enums';
import {
    NewRelicEvent,
    getNewRelicPerformanceEvents,
    getNavigatorNetworkInformation,
    isPerformanceResourceTiming,
} from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import { isAppVisible } from '@mews/std';
import { getCurrentUrl, anonymizeUrl } from '@mews-commander/utils';
import {
    getCurrentEnterpriseId,
    getCurrentProfileIdOrNull,
    getSessionId,
    getCurrentUserOrNull,
} from '@mews-commander/core';
import { getCurrentEnterpriseCountryCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getEnterpriseName, getEnterpriseUniqueName } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { getVersion, getPlatformApiUrl, getPlatformEnvironment } from '../reducers/configuration';

export const normalizeUrl = (url: string) => {
    const anonymizedUrl = anonymizeUrl(url);

    const datesRegex = /\/#\d{4}-\d{2}-\d{2}t\d{2}:\d{2}:\d{2}z\|\d{4}-\d{2}-\d{2}t\d{2}:\d{2}:\d{2}z/;
    const datesMatch = datesRegex.exec(anonymizedUrl);

    if (!datesMatch) {
        return anonymizedUrl;
    }

    return anonymizedUrl.slice(0, datesMatch.index);
};

export const reportNewRelicEvents =
    (events: NewRelicEvent<NewRelicEventType>[]): CommanderThunkAction =>
    (_, getState, { newRelicReporterService, featureFlagService, getRouteInfo }) => {
        if (!featureFlagService.hasFlag(FeatureFlag.isNewRelicEventsAPIEnabled)) {
            return;
        }

        const state = getState();

        const sessionId = getSessionId();

        const userId = getCurrentUserOrNull()?.id ?? null;
        const profileId = getCurrentProfileIdOrNull();
        const enterpriseId = getCurrentEnterpriseId();
        const enterpriseName = getEnterpriseName(state, { enterpriseId });
        const enterpriseUniqueName = getEnterpriseUniqueName(state, { enterpriseId });
        const countryCode = getCurrentEnterpriseCountryCode(state);

        const getRouteAttributes = (url: string) => ({
            url,
            normalizedUrl: normalizeUrl(url),
            ...getRouteInfo(url),
        });

        const appVisible = isAppVisible();
        const environment = getPlatformEnvironment();
        const appVersion = getVersion();

        const enhancedEvents = events.map((event) => ({
            sessionId,
            userId,
            profileId,
            enterpriseId,
            enterpriseName,
            ...(enterpriseUniqueName != null && { enterpriseUniqueName }),
            countryCode,
            ...getRouteAttributes(typeof event.url === 'string' ? event.url : getCurrentUrl()),
            environment,
            isAppVisible: appVisible,
            appName: Application.Commander,
            appVersion,
            ...event,
        }));

        newRelicReporterService.sendEvents(enhancedEvents);
    };

interface WithContext {
    context: Window;
}

export const reportNewRelicPerformanceObserverEvents =
    (entryList: PerformanceObserverEntryList, { context }: WithContext): CommanderThunkAction =>
    (dispatch) => {
        const apiUrl = getPlatformApiUrl();
        const networkInformation = getNavigatorNetworkInformation(context);

        const isNotOtherResourceTiming = (entry: PerformanceEntry) =>
            !isPerformanceResourceTiming(entry) || entry.name.startsWith(apiUrl);
        const entries = array.filter(isNotOtherResourceTiming)(entryList.getEntries());
        const newRelicEvents = getNewRelicPerformanceEvents(entries, { networkInformation });

        dispatch(reportNewRelicEvents(newRelicEvents));
    };
